import { ResumeTemplate } from '../../../mocks/resumeTemplates';

interface FavoritesPanelProps {
  favorites: ResumeTemplate[];
  onRemove: (name: string) => void;
  onPreview: (template: ResumeTemplate) => void;
  onClose: () => void;
}

export default function FavoritesPanel({
  favorites,
  onRemove,
  onPreview,
  onClose,
}: FavoritesPanelProps) {
  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="relative z-10 w-full max-w-sm bg-white h-full flex flex-col shadow-2xl animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Saved Templates</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {favorites.length} template{favorites.length !== 1 ? 's' : ''} saved
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {favorites.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-bookmark-line text-3xl text-amber-400"></i>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">No saved templates yet</h3>
              <p className="text-sm text-gray-400 max-w-[200px]">
                Click the bookmark icon on any template to save it here for comparison.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {favorites.map((template) => (
                <div
                  key={template.name}
                  className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all group"
                >
                  {/* Thumbnail */}
                  <div
                    className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0 relative cursor-pointer"
                    onClick={() => onPreview(template)}
                  >
                    <div
                      className="absolute top-0 left-0 right-0 h-1 z-10"
                      style={{ backgroundColor: template.color }}
                    />
                    <img
                      src={template.image}
                      alt={template.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-gray-900 truncate leading-tight mb-0.5">
                      {template.name}
                    </h4>
                    <p className="text-xs text-gray-400 mb-2 line-clamp-2 leading-relaxed">
                      {template.description}
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: template.color + '18', color: template.color }}
                      >
                        {template.pages}p
                      </span>
                      {template.badge && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          template.badge === 'Popular'
                            ? 'bg-violet-100 text-violet-600'
                            : 'bg-emerald-100 text-emerald-600'
                        }`}>
                          {template.badge === 'Popular' ? '🔥' : '✨'} {template.badge}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => onPreview(template)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-300 transition-colors cursor-pointer"
                      title="Preview"
                    >
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => onRemove(template.name)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer"
                      title="Remove"
                    >
                      <i className="ri-bookmark-fill text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {favorites.length > 0 && (
          <div className="px-4 py-4 border-t border-gray-100 space-y-2">
            <p className="text-xs text-gray-400 text-center">
              Compare your saved templates side by side
            </p>
            <button
              onClick={() => {
                if (window.confirm('Clear all saved templates?')) {
                  favorites.forEach((t) => onRemove(t.name));
                }
              }}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-red-500 border border-red-100 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-1.5"></i>
              Clear All Saved
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
